import { ref, computed, onMounted } from "vue";
import ProjectService from "../services/projectService.js";
import ProfileService from "../services/profileService.js";

export function useHome() {
  const isLoading = ref(false);
  const activeTab = ref("recent"); // "recent" | "starred"
  const recentDocuments = ref([]);
  const starredDocuments = ref([]);
  const currentUser = ref({ firstName: "", email: "" });

  const projectService = new ProjectService();
  const profileService = new ProfileService();

  const timeOfDay = computed(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Morning";
    if (hour < 17) return "Afternoon";
    return "Evening";
  });

  const displayedDocuments = computed(() =>
    activeTab.value === "starred" ? starredDocuments.value : recentDocuments.value
  );

  onMounted(async () => {
    isLoading.value = true;
    await Promise.all([loadUser(), loadRecentDocuments(), loadStarredDocuments()]);
    isLoading.value = false;
  });

  async function loadUser() {
    // Auth0's profile doesn't reliably carry a display name — our own
    // profile (name the user set, or blank) is the source of truth here.
    // getMyProfile() can resolve to undefined on error, so guard the destructure.
    const { name, email = "" } = (await profileService.getMyProfile()) ?? {};
    currentUser.value = {
      firstName: name ? name.split(" ")[0] : email,
      email: email.toLowerCase(),
    };
  }

  async function loadRecentDocuments() {
    const res = await projectService.getRecentDocuments();
    recentDocuments.value = res.documents ?? [];
  }

  async function loadStarredDocuments() {
    const res = await projectService.getStaredDocuments();
    starredDocuments.value = res.documents ?? [];
  }

  // Switching tabs re-fetches that tab's list in the background so we're
  // showing the latest state rather than whatever was cached at page load.
  async function setTab(tab) {
    activeTab.value = tab;
    if (tab === "starred") await loadStarredDocuments();
    else await loadRecentDocuments();
  }

  // A doc can appear in both lists (recently accessed AND starred), so
  // remove it from whichever it's in rather than assuming just the active tab.
  function removeDocument(docId) {
    recentDocuments.value = recentDocuments.value.filter((d) => d.id !== docId);
    starredDocuments.value = starredDocuments.value.filter((d) => d.id !== docId);
  }

  return {
    isLoading,
    activeTab,
    recentDocuments,
    starredDocuments,
    displayedDocuments,
    currentUser,
    timeOfDay,
    setTab,
    removeDocument,
  };
}
